import { useNavigate } from "react-router-dom";
import { Building, Edit, Globe, Link as LinkIcon } from "@carbon/icons-react";
import { Button, Link, ProgressBar, Tile } from "@carbon/react";
import i18next from "../../i18n";
import { Loader } from "..";
import { useCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import { calculateProfileCompletion } from "../CompanyProfile/companyProfileCompletion";
import "./completionPercentage.scss";

/**
 * The company header + profile-completion card shown on the seller home and the company profile view.
 * Rebuilt on Carbon (company-profile redesign) from the previous hand-rolled Tailwind card
 * (`bg-white rounded-lg shadow` + a green-gradient bar): a raised Carbon `Tile` with the company
 * identity and an Edit action, and a neutral Carbon `ProgressBar` for completion.
 */
const CompletionPercentage = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const { data: companyProfile, isLoading } = useCompanyProfile(
    user?.company?.id || ""
  );

  const completionPercentage = companyProfile
    ? calculateProfileCompletion(companyProfile)
    : 0;

  if (isLoading) return <Loader />;

  return (
    <Tile className="completion-card">
      <div className="completion-card__head">
        <div className="completion-card__identity">
          <span className="completion-card__avatar">
            <Building size={32} />
          </span>
          <div className="completion-card__meta">
            <h2 className="completion-card__name">
              {companyProfile?.commercialName || companyProfile?.name}
            </h2>
            <div className="completion-card__links">
              {companyProfile?.website && (
                <Link
                  href={companyProfile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  renderIcon={Globe}
                >
                  {i18next.t("companyProfile.website")}
                </Link>
              )}
              {companyProfile?.linkedinUrl && (
                <Link
                  href={companyProfile.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  renderIcon={LinkIcon}
                >
                  {i18next.t("companyProfile.linkedin")}
                </Link>
              )}
            </div>
          </div>
        </div>

        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Edit}
          onClick={() => navigate("/company/edit")}
        >
          {i18next.t("cta.editProfile")}
        </Button>
      </div>

      <div className="completion-card__progress">
        <ProgressBar
          label={i18next.t("companyProfile.profileCompletion")}
          helperText={`${completionPercentage}%`}
          value={completionPercentage}
          max={100}
        />
      </div>
    </Tile>
  );
};

export default CompletionPercentage;
